import React from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { UserProfilePreviewRelay } from '@atlassian/user-profile-preview/relay';

import type { individualPersonKnowledgeCardRelayQuery } from './__generated__/individualPersonKnowledgeCardRelayQuery.graphql';

export type IndividualPersonKnowledgeCardRelayProps = {
	accountId: string;
	cloudId: string;
	orgId: string;
	requestingUserId: string;
	sourceProduct?: string;
};

function buildSiteAri(cloudId: string, sourceProduct?: string): string {
	if (sourceProduct) {
		return `ari:cloud:${sourceProduct}::site/${cloudId}`;
	}
	return cloudId;
}

/**
 * Person knowledge card for a single selected user: loads profile, recent work, collaborators,
 * and reporting lines via one Relay query and {@link UserProfilePreviewRelay}.
 * Must render under a Relay environment and a React `Suspense` boundary (see `PersonKnowledgeCard`).
 */
export function IndividualPersonKnowledgeCardRelay(props: IndividualPersonKnowledgeCardRelayProps) {
	const { accountId, cloudId, orgId, requestingUserId, sourceProduct } = props;
	const accountIdARI = `ari:cloud:identity::user/${accountId}`;

	const data = useLazyLoadQuery<individualPersonKnowledgeCardRelayQuery>(
		graphql`
			query individualPersonKnowledgeCardRelayQuery(
				$first: Int!
				$accountIdARI: ID!
				$filter: ActivityFilter
				$recommendationsQuery: SmartsRecommendationsQuery!
				$reportingLinesAaid: String!
				$reportingLinesOrgId: String!
				$reportingLinesSiteId: String!
			) {
				user(accountId: $accountIdARI) {
					...UserProfilePreviewRelay_user
				}
				activity {
					workedOn(first: $first, filter: $filter) {
						...UserProfilePreviewRelay_workedOn
					}
				}
				smarts {
					...UserProfilePreviewRelay_smarts @arguments(recommendationsQuery: $recommendationsQuery)
				}
				knowledgeDiscovery {
					...UserProfilePreviewRelay_reportingLines
						@arguments(
							aaid: $reportingLinesAaid
							orgId: $reportingLinesOrgId
							siteId: $reportingLinesSiteId
						)
				}
			}
		`,
		{
			first: 50,
			accountIdARI,
			filter: { actors: [accountIdARI] },
			recommendationsQuery: {
				context: {
					userId: accountId,
					tenantId: cloudId,
				},
				modelRequestParams: {
					caller: 'search-ai',
					experience: 'CgUserNearbyUser',
				},
				requestingUserId,
				maxNumberOfResults: 8,
			},
			reportingLinesAaid: accountId,
			reportingLinesOrgId: orgId,
			reportingLinesSiteId: buildSiteAri(cloudId, sourceProduct),
		},
	);

	return (
		<UserProfilePreviewRelay
			userFragment={data.user ?? undefined}
			workedOnFragment={data.activity?.workedOn ?? undefined}
			smartsFragment={data.smarts ?? undefined}
			reportingLinesFragment={data.knowledgeDiscovery ?? undefined}
			cloudId={cloudId}
			testId="individual-person-knowledge-card"
		/>
	);
}
